import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'

export interface MasterInboxReply {
  id: string
  created_at: string
  updated_at: string
  data?: any
  date?: string
  text?: string
  subject?: string
  seen?: number
  name?: string
  first_name?: string
  last_name?: string
  email?: string
  from_name?: string
  from_address?: string
  to_name?: string
  to_address?: string
  job_title?: string | null
  linkedin?: string | null
  campaign_id?: number
  campaign_name?: string
  workspace_id?: number
  workspace_name?: string
  lead_id?: string
  prospect_id?: string
  list_name?: string | null
  channel?: string
  channel_id?: string
  channel_type?: string
  event_type?: string
  reply_from?: string
  triggered_by?: string
  conversation_stage?: string
  message_quantity?: number
  deal_value?: number
  label_names?: any
  thread_url?: string
  sent_message?: string | null
  reply_message?: string | null
  original_subject?: string | null
  has_outbound_message?: boolean | null
  api_response_get_messages?: any | null
  label_name?: string | null
  label_id?: number | null
  reply_category_type?: string | null
  sub_label?: string | null
  categorization_reasoning?: string | null
  categorization_response_text?: string | null
  categorized_at?: string | null
  categorization_model?: string | null
  reply_count?: number | null
  is_human_reply?: boolean | null
  masterinbox_sync_status?: string | null
  masterinbox_sync_attempted_at?: string | null
  masterinbox_sync_error?: string | null
}

export interface MasterInboxKPIs {
  todayTotal: number
  totalAll: number
  emailCount: number
  linkedinCount: number
}

export interface MasterInboxFilters {
  channel: 'email' | 'linkedin'
  replyType: 'all' | 'positive' | 'negative' | 'ooo_bounced' | 'ai_messup' | 'uncategorized'
  humanFilter: 'all' | 'human' | 'auto'
  search: string
  page: number
  pageSize: number
}

const SELECT_COLUMNS = `
  id, created_at, updated_at, date, text, subject, seen,
  name, first_name, last_name, email, from_name, from_address,
  to_name, to_address, job_title, linkedin,
  campaign_id, campaign_name, workspace_id, workspace_name,
  lead_id, prospect_id, list_name, channel, channel_id, channel_type,
  event_type, reply_from, triggered_by, conversation_stage,
  message_quantity, deal_value, label_names, thread_url,
  original_subject, has_outbound_message,
  label_name, label_id, reply_category_type, sub_label,
  categorization_reasoning, categorized_at, categorization_model,
  reply_count, is_human_reply,
  masterinbox_sync_status, masterinbox_sync_attempted_at, masterinbox_sync_error
`

/**
 * Applies channel filter to a query.
 * Email = NOT heyreach channel_id. LinkedIn = heyreach channel_id.
 */
function applyChannelFilter(query: any, channel: 'email' | 'linkedin') {
  if (channel === 'linkedin') {
    return query.ilike('channel_id', '%heyreach%')
  }
  // Email: exclude heyreach. Use .or() to include null channel_id (legacy rows) + non-heyreach
  return query.or('channel_id.is.null,channel_id.not.ilike.%heyreach%')
}

/**
 * Applies reply type and human/auto filters to a query.
 */
function applyReplyFilters(query: any, replyType: string, humanFilter: string) {
  if (replyType !== 'all') {
    if (replyType === 'ooo_bounced') {
      query = query.in('reply_category_type', ['ooo_bounced', 'OOO', 'auto-reply'])
    } else if (replyType === 'ai_messup') {
      query = query.in('reply_category_type', ['ai_messup', 'neutral'])
    } else if (replyType === 'uncategorized') {
      query = query.is('reply_category_type', null)
    } else {
      query = query.eq('reply_category_type', replyType)
    }
  }

  if (humanFilter === 'human') {
    query = query.eq('is_human_reply', true)
  } else if (humanFilter === 'auto') {
    query = query.eq('is_human_reply', false)
  }

  return query
}

/**
 * Server-side paginated Master Inbox replies hook.
 *
 * @param workspaceName - Filter by workspace_name.
 *   undefined = fetch all (admin), null = skip (workspace loading), string = specific client
 * @param filters - Server-side filters (channel, replyType, humanFilter, search, page, pageSize)
 */
export function useMasterInboxReplies(
  workspaceName?: string | null,
  filters?: MasterInboxFilters
) {
  const [replies, setReplies] = useState<MasterInboxReply[]>([])
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [kpis, setKpis] = useState<MasterInboxKPIs>({ todayTotal: 0, totalAll: 0, emailCount: 0, linkedinCount: 0 })

  const channel = filters?.channel ?? 'email'
  const replyType = filters?.replyType ?? 'all'
  const humanFilter = filters?.humanFilter ?? 'all'
  const search = filters?.search ?? ''
  const page = filters?.page ?? 1
  const pageSize = filters?.pageSize ?? 25

  const fetchKPIs = useCallback(async () => {
    if (workspaceName === null) return

    try {
      // Build base filter for all KPI queries
      const baseFilter = (q: any) => {
        if (workspaceName) q = q.eq('workspace_name', workspaceName)
        return q
      }

      const todayUtc = new Date()
      todayUtc.setUTCHours(0, 0, 0, 0)
      const todayStr = todayUtc.toISOString()

      // Run all 4 counts in parallel
      const [todayRes, totalRes, emailRes, linkedinRes] = await Promise.all([
        // Today's replies
        baseFilter(supabase.from('masterinbox_replies').select('id', { count: 'exact', head: true }).gte('created_at', todayStr)),
        // Total all time
        baseFilter(supabase.from('masterinbox_replies').select('id', { count: 'exact', head: true })),
        // Email count
        baseFilter(supabase.from('masterinbox_replies').select('id', { count: 'exact', head: true }).or('channel_id.is.null,channel_id.not.ilike.%heyreach%')),
        // LinkedIn count
        baseFilter(supabase.from('masterinbox_replies').select('id', { count: 'exact', head: true }).ilike('channel_id', '%heyreach%')),
      ])

      setKpis({
        todayTotal: todayRes.count || 0,
        totalAll: totalRes.count || 0,
        emailCount: emailRes.count || 0,
        linkedinCount: linkedinRes.count || 0,
      })
    } catch (err) {
      console.error('Error fetching KPIs:', err)
    }
  }, [workspaceName])

  const fetchPage = useCallback(async (isBackgroundRefresh = false) => {
    if (workspaceName === null) {
      setLoading(false)
      return
    }

    try {
      if (isBackgroundRefresh) {
        setIsRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      // 1. Count query (with all filters applied)
      let countQuery = supabase
        .from('masterinbox_replies')
        .select('id', { count: 'exact', head: true })

      if (workspaceName) countQuery = countQuery.eq('workspace_name', workspaceName)
      countQuery = applyChannelFilter(countQuery, channel)
      countQuery = applyReplyFilters(countQuery, replyType, humanFilter)

      if (search.trim()) {
        // Server-side search using OR across key fields
        const s = `%${search.trim()}%`
        countQuery = countQuery.or(
          `email.ilike.${s},name.ilike.${s},subject.ilike.${s},campaign_name.ilike.${s},from_name.ilike.${s},to_name.ilike.${s}`
        )
      }

      const { count: filteredCount, error: countError } = await countQuery
      if (countError) throw countError
      setTotalCount(filteredCount || 0)

      // 2. Data query (single page)
      let dataQuery = supabase
        .from('masterinbox_replies')
        .select(SELECT_COLUMNS)

      if (workspaceName) dataQuery = dataQuery.eq('workspace_name', workspaceName)
      dataQuery = applyChannelFilter(dataQuery, channel)
      dataQuery = applyReplyFilters(dataQuery, replyType, humanFilter)

      if (search.trim()) {
        const s = `%${search.trim()}%`
        dataQuery = dataQuery.or(
          `email.ilike.${s},name.ilike.${s},subject.ilike.${s},campaign_name.ilike.${s},from_name.ilike.${s},to_name.ilike.${s}`
        )
      }

      const offset = (page - 1) * pageSize
      const { data, error: fetchError } = await dataQuery
        .order('created_at', { ascending: false })
        .range(offset, offset + pageSize - 1)

      if (fetchError) throw fetchError

      setReplies((data || []) as MasterInboxReply[])
    } catch (err: any) {
      console.error('Error fetching master inbox replies:', err)
      setError(err.message || 'Failed to fetch replies')
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [workspaceName, channel, replyType, humanFilter, search, page, pageSize])

  // Fetch KPIs on workspace change
  useEffect(() => {
    fetchKPIs()
  }, [fetchKPIs])

  // Fetch page data when filters/page change
  useEffect(() => {
    fetchPage()
  }, [fetchPage])

  return {
    replies,
    loading,
    isRefreshing,
    error,
    count: totalCount,
    kpis,
    refetch: () => { fetchPage(true); fetchKPIs() },
  }
}
